import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { formatAmountScale, multiplyAmountScale } from "@/shared/lib/amount";
import { formatDateDisplay } from "@/shared/lib/date-utils";
import { labelOr } from "@/shared/lib/label";
import type { ExternalTrade } from "@/features/portfolio/api/external-trade";
import { tradeStatusBadgeVariant } from "@/features/portfolio/lib/trade-status";
import { TransactionLogs, type LogsState } from "./TransactionLogs";

const SIDE_VARIANT: Record<string, "default" | "secondary"> = {
  buy: "default",
  sell: "secondary",
};

interface TransactionCardProps {
  trade: ExternalTrade;
  portfolioName: string | null;
  showPortfolio: boolean;
  isExpanded: boolean;
  logs: LogsState | undefined;
  onToggle: () => void;
  onRetryLogs: () => void;
}

export const TransactionCard = ({
  trade,
  portfolioName,
  showPortfolio,
  isExpanded,
  logs,
  onToggle,
  onRetryLogs,
}: TransactionCardProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();
  const { numberFormat } = useNumberFormat();

  const gross = multiplyAmountScale(
    trade.symbol_amount,
    trade.symbol_scale,
    trade.price_amount,
    trade.price_scale,
    trade.price_scale,
  );

  return (
    <Card className="bg-card border-border p-6" data-testid="transaction-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-foreground">
              {trade.symbol || trade.instrument_id}
            </h3>
            <Badge variant={SIDE_VARIANT[trade.side] || "secondary"}>
              {labelOr(t, `portfolio.side.${trade.side}`, trade.side)}
            </Badge>
            <Badge variant={tradeStatusBadgeVariant(trade.status)} data-testid="transaction-status">
              {labelOr(t, `status.${trade.status}`, trade.status)}
            </Badge>
          </div>
          {showPortfolio && (
            <p className="text-sm text-muted-foreground">
              {t("investorDetail.colPortfolio")}: {portfolioName || trade.portfolio_id}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {formatDateDisplay(trade.reference_time, dateFormat)}
          </p>
        </div>

        <div className="text-left sm:text-right">
          <p className="text-2xl font-bold tabular-nums">
            {gross ? (
              <>
                <span data-testid="transaction-gross">
                  {formatAmountScale(gross.amount, gross.scale, numberFormat)}
                </span>{" "}
                <span className="text-base font-normal text-muted-foreground">{trade.currency}</span>
              </>
            ) : (
              <span
                className="text-base font-normal text-muted-foreground"
                data-testid="transaction-gross-unavailable"
              >
                {t("investorDetail.grossUnavailable")}
              </span>
            )}
          </p>
          <p className="text-sm text-muted-foreground tabular-nums">
            <span data-testid="transaction-quantity">
              {formatAmountScale(trade.symbol_amount, trade.symbol_scale, numberFormat, { trimZeros: true })}
            </span>
            {" × "}
            <span data-testid="transaction-price">
              {formatAmountScale(trade.price_amount, trade.price_scale, numberFormat)}
            </span>
          </p>
        </div>
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-0 hover:bg-transparent"
          onClick={onToggle}
          aria-expanded={isExpanded}
          data-testid="transaction-logs-toggle"
        >
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {t("investorDetail.logsTitle")}
        </Button>

        {isExpanded && <TransactionLogs state={logs} onRetry={onRetryLogs} />}
      </div>
    </Card>
  );
};
